/*
 Copyright (c) 2012, Vladimir Agafonkin
 Simplify.js is a high-performance polyline simplification library
 mourner.github.com/simplify-js
 */

import buckets from 'buckets-js';

export default {
    simplify: simplify,
    simplifyN: simplifyN,
};

// to suit your point format, run search/replace for '.x' and '.y'
// to switch to 3D, uncomment the lines in the next 2 functions
// (configurability would draw significant performance overhead)

function getSquareDistance(p1, p2, xProp, yProp) {
    // square distance between 2 points

    var dx = getValueByProp(p1, xProp) - getValueByProp(p2, xProp),
        //      dz = p1.z - p2.z,
        dy = getValueByProp(p1, yProp) - getValueByProp(p2, yProp);

    return (
        dx * dx +
        //         dz * dz +
        dy * dy
    );
}

function getValueByProp(data, props) {
    return props.reduce(function (p, prop) {
        return p[prop];
    }, data);
}

function getSquareSegmentDistance(p, p1, p2, xProp, yProp) {
    // square distance from a point to a segment

    var x = getValueByProp(p1, xProp),
        y = getValueByProp(p1, yProp),
        p2x = getValueByProp(p2, xProp),
        p2y = getValueByProp(p2, yProp),
        //      z = p1.z,

        //      dz = p2.z - z,

        t;

    if (x === undefined || y === undefined) {
        // ! Prevents line drawing errors if there are no values for given points
        return 1;
    }

    var px = getValueByProp(p, xProp),
        py = getValueByProp(p, yProp),
        dx = p2x - x,
        dy = p2y - y;

    if (dx !== 0 || dy !== 0) {
        t =
            ((px - x) * dx +
                //         (p.z - z) * dz +
                (py - y) * dy) /
            (dx * dx +
                //             dz * dz +
                dy * dy);

        if (t > 1) {
            x = p2x;
            y = p2y;
        } else if (t > 0) {
            x += dx * t;
            y += dy * t;
        }
    }

    dx = px - x;
    dy = py - y;

    return (
        dx * dx +
        //         dz * dz +
        dy * dy
    );
}

// the rest of the code doesn't care for the point format

function simplifyRadialDistance(points, sqTolerance, xProp, yProp) {
    // distance-based simplification

    var i,
        len = points.length,
        point,
        prevPoint = points[0],
        newPoints = [prevPoint];

    for (i = 1; i < len; i++) {
        point = points[i];

        if (getSquareDistance(point, prevPoint, xProp, yProp) > sqTolerance) {
            newPoints.push(point);
            prevPoint = point;
        }
    }

    if (prevPoint !== point) {
        newPoints.push(point);
    }

    return newPoints;
}

// simplification using optimized Douglas-Peucker algorithm with recursion elimination

function simplifyDouglasPeucker(points, sqTolerance, xProp, yProp) {
    var len = points.length,
        MarkerArray = typeof Uint8Array !== undefined + '' ? Uint8Array : Array,
        markers = new MarkerArray(len),
        first = 0,
        last = len - 1,
        i,
        key,
        firstStack = [],
        lastStack = [],
        newPoints = [];

    markers[first] = markers[last] = 1;

    while (last) {
        key = findKey(points, first, last, xProp, yProp);

        if (key.maxSqDist > sqTolerance) {
            markers[key.index] = 1;

            firstStack.push(first);
            lastStack.push(key.index);

            firstStack.push(key.index);
            lastStack.push(last);
        }

        first = firstStack.pop();
        last = lastStack.pop();
    }

    for (i = 0; i < len; i++) {
        if (markers[i]) {
            newPoints.push(points[i]);
        }
    }

    return newPoints;
}

function simplifyDouglasPeuckerN(points, pointsCount, xProp, yProp) {
    if (!points) {
        return [];
    }

    var len = points.length,
        MarkerArray = typeof Uint8Array !== undefined + '' ? Uint8Array : Array,
        markers = new MarkerArray(len),
        first = 0,
        last = len - 1,
        i,
        key,
        keyCounts = 0,
        newPoints = [];

    markers[first] = markers[last] = 1;

    if (pointsCount > 2) {
        key = findKey(points, first, last, xProp, yProp);
        var subPoly = {
            key: key,
            first: first,
            last: last,
        };
        var queue = buckets.PriorityQueue((a, b) => {
            if (a.key.maxSqDist > b.key.maxSqDist) {
                return 1;
            }

            if (a.key.maxSqDist < b.key.maxSqDist) {
                return -1;
            }

            return 0;
        });
        queue.add(subPoly);
        while (!queue.isEmpty()) {
            subPoly = queue.dequeue();
            markers[subPoly.key.index] = 1;
            keyCounts++;

            if (keyCounts == pointsCount) {
                break;
            }

            var leftSubPoly = {
                first: subPoly.first,
                last: subPoly.key.index,
            };
            leftSubPoly.key = findKey(
                points,
                leftSubPoly.first,
                leftSubPoly.last,
                xProp,
                yProp
            );
            if (leftSubPoly.key.index) {
                queue.add(leftSubPoly);
            }

            var rightSubPoly = {
                first: subPoly.key.index,
                last: subPoly.last,
            };
            rightSubPoly.key = findKey(
                points,
                rightSubPoly.first,
                rightSubPoly.last,
                xProp,
                yProp
            );
            if (rightSubPoly.key.index) {
                queue.add(rightSubPoly);
            }
        }
    }
    for (i = 0; i < len; i++) {
        if (markers[i]) {
            newPoints.push(points[i]);
        }
    }

    return newPoints;
}

function findKey(points, first, last, xProp, yProp) {
    var i, index;
    var maxSqDist = 0,
        sqDist;
    for (i = first + 1; i < last; i++) {
        sqDist = getSquareSegmentDistance(
            points[i],
            points[first],
            points[last],
            xProp,
            yProp
        );
        if (sqDist > maxSqDist) {
            index = i;
            maxSqDist = sqDist;
        }
    }
    return { maxSqDist: maxSqDist, index: index };
}

function simplify(points, tolerance, highestQuality, xProp = 'x', yProp = 'y') {
    xProp = xProp.split('.');
    yProp = yProp.split('.');

    var sqTolerance = tolerance !== undefined ? tolerance * tolerance : 1;

    if (!highestQuality) {
        points = simplifyRadialDistance(points, sqTolerance, xProp, yProp);
    }
    points = simplifyDouglasPeucker(points, sqTolerance, xProp, yProp);

    return points;
}

function simplifyN(points, count, xProp = 'x', yProp = 'y') {
    xProp = xProp.split('.');
    yProp = yProp.split('.');

    points = simplifyDouglasPeuckerN(points, count, xProp, yProp);

    return points;
}
